import { TFunction } from 'i18next';
import { Step } from 'react-joyride';

import { Brand, SingleAssignment } from 'shared/api/assignment-service';

import walkthroughAssignmentThumbnailPath from './ui/assets/walkthrough-assignment-thumbnail.jpg';
import { WalkthroughProducts, WalkthroughType } from './types';

export const WT_DEFAULT_SCROLL_OFFSET = 100;

export const WT_PROGRAMS_SCROLL_OFFSET = 400;

export const WT_PROGRAMS_COLLAPSE_ANIMATION_DURATION = 500;

const overlayStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    filter: 'blur(2px)',
  },
};

export const WALKTHROUGH_CONFIGS = {
  [WalkthroughType.FULL]: {
    type: WalkthroughType.FULL,
    count: 4,
  },
  [WalkthroughType.FULL_NO_PROGRAMS]: {
    type: WalkthroughType.FULL_NO_PROGRAMS,
    count: 3,
  },
  [WalkthroughType.SHORT]: {
    type: WalkthroughType.SHORT,
    count: 3,
  },
  [WalkthroughType.SHORT_NO_PROGRAMS]: {
    type: WalkthroughType.SHORT_NO_PROGRAMS,
    count: 2,
  },
} as const;

export const getWalkthroughSteps = (t: TFunction<'translation', undefined>) => {
  const programsSectionStep: Step = {
    target: '#programs_section',
    content: t(
      'Welcome to Educator Home! Here are each of your programs. ' +
        'Click on one to jump in and quickly access what you need for each day.',
    ),

    disableBeacon: true,
    placement: 'right',
    styles: overlayStyles,
    floaterProps: {
      disableAnimation: true,
    },
  };

  const createRecommendationsSectionStep = (isFirst: boolean): Step => ({
    target: '#recommendations_section',
    content: t(
      'Here is where you will find suggested next steps, reminders, and notifications about helpful resources.',
    ),
    placement: 'bottom',
    styles: overlayStyles,
    disableBeacon: isFirst,
    floaterProps: {
      disableFlip: true,
      disableAnimation: true,
    },
  });

  const streamSectionStep: Step = {
    target: '#stream_section',
    content: t(
      'Here you will find your class activity. ' +
        'Click “See All” to view your activity since the beginning of the school year. ' +
        'There, you can also filter by class if you have multiple classes.',
    ),
    placement: 'top',
    styles: overlayStyles,
    floaterProps: {
      disableFlip: true,
      disableAnimation: true,
    },
  };

  const quickLinksSectionStep: Step = {
    target: '#quick_links_section',
    content: t(
      'Explore Quick Links for easy access other locations in the Amplify experience.',
    ),
    placement: 'right',
    styles: overlayStyles,
    floaterProps: {
      disableAnimation: true,
    },
  };

  const FULL_WALKTHROUGH_STEPS: Step[] = [
    programsSectionStep,
    createRecommendationsSectionStep(false),
    streamSectionStep,
    quickLinksSectionStep,
  ];

  const SHORT_WALKTHROUGH_STEPS: Step[] = [
    programsSectionStep,
    createRecommendationsSectionStep(false),
    quickLinksSectionStep,
  ];

  const FULL_WALKTHROUGH_STEPS_WITHOUT_PROGRAMS: Step[] = [
    createRecommendationsSectionStep(true),
    streamSectionStep,
    quickLinksSectionStep,
  ];

  const SHORT_WALKTHROUGH_STEPS_WITHOUT_PROGRAMS: Step[] = [
    createRecommendationsSectionStep(true),
    quickLinksSectionStep,
  ];

  return {
    fullWalkthroughSteps: FULL_WALKTHROUGH_STEPS,
    shortWalkthroughSteps: SHORT_WALKTHROUGH_STEPS,
    fullWalkthroughStepsWithoutPrograms:
      FULL_WALKTHROUGH_STEPS_WITHOUT_PROGRAMS,
    shortWalkthroughStepsWithoutPrograms:
      SHORT_WALKTHROUGH_STEPS_WITHOUT_PROGRAMS,
  };
};

export const WT_CKLA_ASSETS_IDS = [
  '546aa88f-edc3-48f1-a72e-e8b316001bab',
  '1e644264-8c97-4b79-89ad-5bfb6634d048',
  'e155def6-9333-426f-b6c0-b66b52cf5914',
  '5dc255d0-3f50-4c55-a3b2-cb7b6d4d08ae',
  '2b79261f-87a5-4cb3-bb69-f433b6c585a7',
  'e01c88ac-eaf4-416f-b260-8f2c4b653075',
];

export const WT_CAMINOS_ASSETS_IDS = [
  'c2ee3431-06c3-4712-ac77-b12bd82c43d9',
  '9bfa8113-cd00-4492-a890-3d0e2c311fc8',
  'f21500d5-64e7-4618-9370-c27453c09c12',
  'cff3ec83-f48a-4baf-b89a-b6ef098b0136',
  '9796a681-6880-488a-adf8-593f371e535a',
  '58893927-7f6f-4974-9e86-f89cc2ba1575',
];

export const WT_ELA_ASSETS_IDS = ['ab3e65a5-e27c-4495-a1eb-469d36e3a0d6'];

export const WT_ADM_ASSETS_IDS = ['2852f8f4-3d3b-414b-affa-17b801e6c60a'];

export const WT_SCIENCE_ASSETS_IDS = [
  '6e74e98d-3e49-4612-8211-18779af0769a',
  '09a07a8f-0669-4938-97ec-0e5f466b9874',
];

// Elementary Literacy Program
export const WT_ELP_ASSETS_IDS = ['20b2d235-7005-4991-bfec-a289b6439aa9'];

export const WT_TX_ELAR_MS_ASSETS_IDS = [
  '1fcad967-4656-4b63-98c6-566a44e19e9b',
];

export const WALKTHROUGH_PRODUCTS_IDS_MAP: Record<
  WalkthroughProducts,
  string[]
> = {
  [WalkthroughProducts.CKLA]: WT_CKLA_ASSETS_IDS,
  [WalkthroughProducts.CAMINOS]: WT_CAMINOS_ASSETS_IDS,
  [WalkthroughProducts.ELA]: WT_ELA_ASSETS_IDS,
  [WalkthroughProducts.DESMOS_MATH]: WT_ADM_ASSETS_IDS,
  [WalkthroughProducts.SCIENCE]: WT_SCIENCE_ASSETS_IDS,
  [WalkthroughProducts.ELP]: WT_ELP_ASSETS_IDS,
  [WalkthroughProducts.TEXAS_ELAR]: WT_TX_ELAR_MS_ASSETS_IDS,
};

const WT_BASE_ASSIGNMENT: SingleAssignment = {
  type: 'Lesson',
  businessKey: 'WT_CKLA_ASSIGNMENT',
  classBks: ['fake-class-bk'],
  createdDate: 1677664380000,
  creatorBk: 'fake-creator-bk',
  lastUpdated: 1677664380000,
  content: {
    brand: Brand.REGULAR,
    contentBusinessKey: '',
    domain: 'CKLA',
    lessonThumbnailImagePath: walkthroughAssignmentThumbnailPath,
    unitTitle: 'Unit 1',
    lessonTitle: 'Lesson 1',
    unitKey: 'fake-unit-key',
    chapterKey: 'fake-chapter-key',
    lessonKey: 'fake-lesson-key',
  },
  dueDate: 1677750780000,
  startDate: 1678873980000,
};

const WT_CKLA_ASSIGNMENT: SingleAssignment = {
  ...WT_BASE_ASSIGNMENT,
  businessKey: 'WT_CKLA_ASSIGNMENT',
  content: {
    ...WT_BASE_ASSIGNMENT.content,
    domain: 'CKLA',
  },
};

const WT_CAMINOS_ASSIGNMENT: SingleAssignment = {
  ...WT_BASE_ASSIGNMENT,
  businessKey: 'WT_CAMINOS_ASSIGNMENT',
  content: {
    ...WT_BASE_ASSIGNMENT.content,
    domain: 'Caminos',
  },
};

const WT_ELA_ASSIGNMENT: SingleAssignment = {
  ...WT_BASE_ASSIGNMENT,
  businessKey: 'WT_ELA_ASSIGNMENT',
  content: {
    ...WT_BASE_ASSIGNMENT.content,
    domain: 'ELA',
  },
};

const WT_DESMOS_MATH_ASSIGNMENT: SingleAssignment = {
  ...WT_BASE_ASSIGNMENT,
  businessKey: 'WT_DESMOS_MATH_ASSIGNMENT',
  content: {
    ...WT_BASE_ASSIGNMENT.content,
    domain: 'Math',
  },
};

const WT_SCIENCE_ASSIGNMENT: SingleAssignment = {
  ...WT_BASE_ASSIGNMENT,
  businessKey: 'WT_SCIENCE_ASSIGNMENT',
  content: {
    ...WT_BASE_ASSIGNMENT.content,
    domain: 'Science',
  },
};

const WT_ELP_ASSIGNMENT: SingleAssignment = {
  ...WT_BASE_ASSIGNMENT,
  businessKey: 'WT_ELP_ASSIGNMENT',
  content: {
    ...WT_BASE_ASSIGNMENT.content,
    domain: 'Elementary Literacy Program',
  },
};

const WT_TEXAS_ELAR_ASSIGNMENT: SingleAssignment = {
  ...WT_BASE_ASSIGNMENT,
  businessKey: 'WT_TEXAS_ELAR_ASSIGNMENT',
  content: {
    ...WT_BASE_ASSIGNMENT.content,
    domain: 'TX ELAR 6-8',
  },
};

export const WALKTHROUGH_ALL_ASSIGNMENTS_BY_PRODUCT_MAP: Record<
  string,
  SingleAssignment
> = {
  [WalkthroughProducts.CKLA]: WT_CKLA_ASSIGNMENT,
  [WalkthroughProducts.CAMINOS]: WT_CAMINOS_ASSIGNMENT,
  [WalkthroughProducts.ELA]: WT_ELA_ASSIGNMENT,
  [WalkthroughProducts.DESMOS_MATH]: WT_DESMOS_MATH_ASSIGNMENT,
  [WalkthroughProducts.SCIENCE]: WT_SCIENCE_ASSIGNMENT,
  [WalkthroughProducts.ELP]: WT_ELP_ASSIGNMENT,
  [WalkthroughProducts.TEXAS_ELAR]: WT_TEXAS_ELAR_ASSIGNMENT,
};
